import { useEffect, useState, useCallback } from "react";
import { theme, card, emptyFrame, inputStyle } from "../theme";
import { api, apiBlob, apiErrorMessage } from "../api/client";
import { auditActionLabel, readableAuditValue, readableFieldName } from "../lib/auditAction";

import { cbPalette } from "../lib/colourBlind";
interface LogEntry {
  id: string;
  role: string;
  category: string;
  action: string;
  detail: string | null;
  createdAt: string;
  // Who acted; `removed` when the account is gone and the name is the one recorded at the time. Null: the platform.
  actor?: { name: string | null; email: string | null; removed: boolean } | null;
  // What an edit changed, field by field (secrets recorded only as changed).
  changes?: Array<{ field: string; before: unknown; after: unknown }>;
}

/** The part of an edit's detail that names what was edited, before its "field: before → after" summary. */
function changeSubject(detail: string | null, firstField: string): string | null {
  if (!detail) return null;
  const at = detail.indexOf(`${firstField}: `);
  return at > 0 ? detail.slice(0, at).replace(/[:\s]+$/, "") || null : null;
}

const showValue = readableAuditValue;

/** Always offered; any other category or role the log holds is added from the server's facets. */
const CATEGORIES = ["auth", "audit", "template", "document", "review", "comment", "profile", "settings", "schedule"];
const ROLES = ["client", "consultant", "auditor"];
const capitalise = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);

const CATEGORY_COLOR: Record<string, string> = cbPalette({
  auth:     "#6366f1",
  audit:    "#a855f7",
  template: "#3b82f6",
  document: "#0ea5e9",
  review:   "#22c55e",
  comment:  "#f59e0b",
  profile:  "#64748b",
  settings: "#64748b",
  schedule: "#ec4899",
});

const ROLE_COLOR: Record<string, { color: string; bg: string }> = cbPalette({
  client:     { color: "#3b82f6", bg: "#3b82f622" },
  consultant: { color: "#a855f7", bg: "#a855f722" },
  auditor:    { color: "#22c55e", bg: "#22c55e22" },
});

function categoryColor(cat: string) {
  return CATEGORY_COLOR[cat] ?? theme.color.accent;
}

export function ActivityLogPage() {
  const [entries, setEntries] = useState<LogEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  // The range typed into the boxes, applied only by Apply (the list does not reload on every keystroke).
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateError, setDateError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [fetchError, setFetchError] = useState(false);
  const [facets, setFacets] = useState<{ categories: string[]; roles: string[] }>({ categories: [], roles: [] });

  const buildParams = useCallback((extraPage?: number): Record<string, string> => {
    const p: Record<string, string> = { page: String(extraPage ?? page) };
    if (categoryFilter !== "all") p.category = categoryFilter;
    if (roleFilter !== "all") p.role = roleFilter;
    if (dateFrom) p.dateFrom = dateFrom;
    if (dateTo)   p.dateTo   = dateTo;
    return p;
  }, [page, categoryFilter, roleFilter, dateFrom, dateTo]);

  useEffect(() => {
    // Only the latest filter's answer is shown: a slower reply to an earlier click is dropped.
    let current = true;
    setEntries(null);
    setFetchError(false);
    api.get<{ entries: LogEntry[]; total: number; facets?: { categories: string[]; roles: string[] } }>("/audit-log", buildParams()).then((d) => {
      if (!current) return;
      setEntries(d.entries);
      setTotal(d.total);
      if (d.facets) setFacets(d.facets);
    }).catch(() => {
      if (!current) return;
      setFetchError(true);
      setEntries([]);
    });
    return () => { current = false; };
  }, [buildParams]);

  const categories = [...CATEGORIES, ...facets.categories.filter((c) => !CATEGORIES.includes(c)).sort()];
  const roles = [...ROLES, ...facets.roles.filter((r) => !ROLES.includes(r)).sort()];

  function setCategory(cat: string) { setCategoryFilter(cat); setPage(0); }
  function setRole(role: string) { setRoleFilter(role); setPage(0); }
  function applyDates() {
    if (draftFrom && draftTo && draftFrom > draftTo) { setDateError("The start date is after the end date."); return; }
    setDateError("");
    setDateFrom(draftFrom); setDateTo(draftTo); setPage(0);
  }
  function clearDates() { setDraftFrom(""); setDraftTo(""); setDateFrom(""); setDateTo(""); setDateError(""); setPage(0); }

  async function handleExport() {
    setExporting(true);
    setExportError("");
    try {
      const params = buildParams(0);
      const blob = await apiBlob("/audit-log/export", params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(apiErrorMessage(e, "Could not export the activity log. Please try again."));
    } finally {
      setExporting(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / 25));

  const filterChip = (active: boolean): React.CSSProperties => ({
    padding: "6px 12px", borderRadius: 3, fontSize: 11, fontWeight: 600, cursor: "pointer",
    border: `1px solid ${active ? theme.color.accent : theme.color.borderStrong}`,
    background: active ? theme.color.accentBg : theme.color.panel,
    color: active ? theme.color.accentText : theme.color.subtle,
  });

  return (
    <div className="page-pad">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Activity Log</h1>
        <button
          onClick={handleExport}
          disabled={exporting}
          style={{ padding: "7px 14px", fontSize: 12, fontWeight: 600, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.text, cursor: exporting ? "not-allowed" : "pointer", opacity: exporting ? 0.6 : 1 }}
        >
          {exporting ? "Exporting…" : "↓ Export CSV"}
        </button>
      </div>
      <p style={{ margin: "0 0 18px", fontSize: 13, color: theme.color.subtle }}>
        {entries ? `${total} entr${total !== 1 ? "ies" : "y"} — ` : ""}append-only, read-only record of everything that has happened on your account.
      </p>

      {/* Date range filter */}
      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0 }}>Date range</div>
        <input
          type="date"
          value={draftFrom}
          aria-label="From"
          onChange={(e) => setDraftFrom(e.target.value)}
          style={{ ...inputStyle, fontSize: 12, padding: "5px 9px", width: 140 }}
        />
        <span style={{ fontSize: 12, color: theme.color.faint }}>to</span>
        <input
          type="date"
          value={draftTo}
          aria-label="To"
          onChange={(e) => setDraftTo(e.target.value)}
          style={{ ...inputStyle, fontSize: 12, padding: "5px 9px", width: 140 }}
        />
        <button
          onClick={applyDates}
          style={{ padding: "5px 12px", fontSize: 12, fontWeight: 600, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-accent)", color: "#fff", cursor: "pointer" }}
        >
          Apply
        </button>
        {(draftFrom || draftTo || dateFrom || dateTo) && (
          <button
            onClick={clearDates}
            style={{ padding: "5px 10px", fontSize: 12, borderRadius: 3, border: "1px solid var(--c-border)", background: "transparent", color: theme.color.muted, cursor: "pointer" }}
          >
            Clear
          </button>
        )}
      </div>

      {dateError && <div role="alert" style={{ marginBottom: 12, fontSize: 12, color: theme.color.dangerText }}>{dateError}</div>}
      {exportError && <div role="alert" style={{ marginBottom: 12, fontSize: 12, color: theme.color.dangerText }}>{exportError}</div>}

      {/* Category filter */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.faint, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Category</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => setCategory("all")} style={filterChip(categoryFilter === "all")}>All</button>
          {categories.map((cat) => (
            <button key={cat} onClick={() => setCategory(cat)} style={filterChip(categoryFilter === cat)}>
              {capitalise(cat)}
            </button>
          ))}
        </div>
      </div>

      {/* Role filter */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.faint, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Role</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["all", ...roles].map((r) => (
            <button key={r} onClick={() => setRole(r)} style={filterChip(roleFilter === r)}>
              {capitalise(r)}
            </button>
          ))}
        </div>
      </div>

      {fetchError ? (
        <div style={{ ...card, textAlign: "center", padding: "32px 20px", color: theme.color.dangerText, fontSize: 13 }}>Could not load the activity log. Please refresh the page.</div>
      ) : !entries ? (
        <div style={{ ...card, textAlign: "center", padding: "32px 20px", color: theme.color.faint, fontSize: 13 }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "48px 20px", color: theme.color.faint, fontSize: 13 }}>
          {total === 0 && categoryFilter === "all" && roleFilter === "all"
            ? "Nothing recorded yet."
            : "No entries match the selected filters."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {entries.map((e) => {
            const roleStyle = ROLE_COLOR[e.role] ?? { color: theme.color.muted, bg: theme.color.panel };
            const catColor = categoryColor(e.category);
            return (
              <div
                key={e.id}
                style={{
                  padding: "12px 16px",
                  background: theme.color.panel,
                  border: `1px solid ${theme.color.border}`,
                  borderRadius: "0 3px 3px 0",
                  borderLeft: `3px solid ${catColor}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, flex: 1 }}>{auditActionLabel(e.action)}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 3, background: catColor + "22", color: catColor }}>
                    {e.category}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 3, background: roleStyle.bg, color: roleStyle.color }}>
                    {e.role}
                  </span>
                </div>
                {(() => {
                  // With a field-by-field list, show only what the detail says before it ("Finance", "Head office (branch, GB)").
                  const text = e.changes?.length ? changeSubject(e.detail, e.changes[0].field) : e.detail;
                  return text ? <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 4 }}>{text}</div> : null;
                })()}
                {e.changes && e.changes.length > 0 && (
                  <div style={{ display: "grid", gap: 2, marginBottom: 4 }}>
                    {e.changes.map((c) => (
                      <div key={c.field} style={{ fontSize: 12, color: theme.color.subtle, wordBreak: "break-word" }}>
                        <span style={{ fontWeight: 600, color: theme.color.text }}>{readableFieldName(c.field)}</span>: {showValue(c.before)} → {showValue(c.after)}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 10, color: theme.color.faint, fontVariantNumeric: "tabular-nums" }}>
                  {new Date(e.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  {e.actor?.name && (
                    <span title={e.actor.email ?? undefined}>
                      {" · "}{e.actor.name}{e.actor.removed ? " (account removed)" : ""}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 16 }}>
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ padding: "6px 14px", background: theme.color.panel, border: `1px solid ${theme.color.border}`, borderRadius: 3, color: theme.color.muted, fontSize: 12, cursor: page === 0 ? "not-allowed" : "pointer", opacity: page === 0 ? 0.5 : 1 }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: 12, color: theme.color.faint }}>Page {page + 1} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={{ padding: "6px 14px", background: theme.color.panel, border: `1px solid ${theme.color.border}`, borderRadius: 3, color: theme.color.muted, fontSize: 12, cursor: page >= totalPages - 1 ? "not-allowed" : "pointer", opacity: page >= totalPages - 1 ? 0.5 : 1 }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
